import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Upload, X, RotateCw, Check, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  getCroppedImg,
  validateFile,
  validateImageDimensions,
  getImageDimensions,
  readFile,
  blobToBase64,
  type Area
} from '@/utils/imageCropUtils';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { logger } from '@/utils/logger';
interface MobileLogoCropUploadProps {
  value: string | null;
  onChange: (logoData: string | null) => void;
  disabled?: boolean;
}

export function MobileLogoCropUpload({ value, onChange, disabled = false }: MobileLogoCropUploadProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dimensionError, setDimensionError] = useState<string | null>(null);

  const TARGET_WIDTH = 220;
  const TARGET_HEIGHT = 90;
  const ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT;

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileValidation = validateFile(file);
    if (!fileValidation.valid) {
      toast({
        title: 'Invalid File',
        description: fileValidation.message,
        variant: 'destructive',
      });
      return;
    }

    try {
      const dimensions = await getImageDimensions(file);
      const dimensionValidation = validateImageDimensions(dimensions.width, dimensions.height);

      if (!dimensionValidation.valid) {
        setDimensionError(dimensionValidation.message || 'Invalid dimensions');
        toast({
          title: 'Image Too Small',
          description: dimensionValidation.message,
          variant: 'destructive',
        });
        return;
      }

      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
      setDimensionError(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCropDialogOpen(true);
    } catch (error) {
      logger.error('Error reading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to read image file. Please try again.',
        variant: 'destructive',
      });
    }

    event.target.value = '';
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const base64 = await blobToBase64(croppedBlob);

      onChange(base64);
      setCropDialogOpen(false);

      toast({
        title: 'Logo Updated',
        description: `Logo cropped to ${TARGET_WIDTH}×${TARGET_HEIGHT}px`,
      });
    } catch (error) {
      logger.error('Error cropping image:', error);
      toast({
        title: 'Crop Failed',
        description: 'Failed to crop image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemove = () => {
    onChange(null);
    toast({
      title: 'Logo Removed',
      description: 'Company logo has been removed',
    });
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Upload PNG, JPG, or SVG. Maximum 5MB.
      </p>

      {value ? (
        <div className="border-2 border-dashed border-border rounded-lg p-4 bg-muted/50">
          {/* Vertical layout for mobile */}
          <div className="flex flex-col items-center gap-3">
            {/* Logo Preview - Centered. The white swatch is deliberate (same as
                desktop LogoCropUpload): logos are previewed against a light
                background so dark/transparent marks stay visible in dark mode. */}
            <div
              className="border rounded bg-white p-2 flex items-center justify-center"
              style={{ width: TARGET_WIDTH, height: TARGET_HEIGHT }}
            >
              <img
                src={value}
                alt="Company logo"
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Status and Actions - Centered */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 text-green-600" />
              <span>Logo set ({TARGET_WIDTH}×{TARGET_HEIGHT}px)</span>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                className="border-input"
              >
                Replace
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
                className="border-input"
              >
                <X className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="w-full h-28 border-2 border-dashed border-border bg-background hover:bg-muted"
          >
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <div className="text-sm text-center">
                <span className="font-medium text-foreground">Tap to upload logo</span>
                <p className="text-xs text-muted-foreground mt-1">
                  Min {TARGET_WIDTH}×{TARGET_HEIGHT}px
                </p>
              </div>
            </div>
          </Button>

          {dimensionError && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">{dimensionError}</AlertDescription>
            </Alert>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Crop Dialog - Mobile optimized */}
      <Dialog open={cropDialogOpen} onOpenChange={setCropDialogOpen}>
        <DialogContent className="dark bg-background text-foreground w-[calc(100%-32px)] max-w-[calc(100%-32px)] max-h-[85vh] overflow-auto rounded-xl">
          <DialogHeader>
            <DialogTitle className="text-foreground">Crop Logo</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Crop Area */}
            <div className="relative w-full h-64 bg-muted rounded-lg overflow-hidden">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={ASPECT_RATIO}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  objectFit="contain"
                />
              )}
            </div>

            {/* Controls */}
            <div className="space-y-4 bg-muted/50 p-3 rounded-lg">
              {/* Zoom Control */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-foreground">Zoom</Label>
                  <span className="text-xs text-muted-foreground">{zoom.toFixed(1)}x</span>
                </div>
                <Slider
                  value={[zoom]}
                  onValueChange={(values) => setZoom(values[0])}
                  min={1}
                  max={3}
                  step={0.1}
                  className="w-full"
                />
              </div>

              {/* Rotate Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRotate}
                className="w-full border-input"
              >
                <RotateCw className="h-4 w-4 mr-2" />
                Rotate 90° (Current: {rotation}°)
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Position your logo within the frame. Final size: {TARGET_WIDTH}×{TARGET_HEIGHT}px
            </p>
          </div>

          <DialogFooter className="flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCropDialogOpen(false)}
              disabled={isProcessing}
              className="flex-1 border-input"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleApplyCrop}
              disabled={isProcessing}
              className="flex-1 bg-primary hover:bg-primary/85 text-primary-foreground"
            >
              {isProcessing ? 'Processing...' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
